<template>
  <div>
    <div class="flex flex-col border rounded bg-gray-50 dark:bg-gray-900" :class="{ 'ring-offset-2 ring-primary-200 ring-4 rounded-md dark:ring-primary-800 dark:ring-offset-gray-900' : isActive }">
      <service-typeahead
        v-if="allowPickerAdd && showTypeahead"
        :selected-ids="selectedServiceIds"
        :type="pickerType"
        :preset-employee="presetEmployee"
        :class="{ 'border-b': displayedServices.length > 0 }"
        @add="handleAddFromPicker"
        @remove="handleRemoveFromPicker"
        @close="handleTypeaheadClose"
        @clear-preset-employee="handleClearPresetEmployee"
      />

      <transition-group type="transition">
        <service
          v-for="entry in displayedServices"
          :key="`service-${entry.key}`"
          :staging="staging"
          :service="entry.service"
          @remove="handleRemoveService(entry.index)"
        />
      </transition-group>

      <p v-if="!displayedServices.length && !allowPickerAdd" class="text-gray-600 text-sm text-center pt-2 pb-3 dark:text-gray-300">
        {{ $t('components.reservation.create.services.noServices') }}
      </p>
    </div>

    <div class="mt-4 flex items-center justify-end space-x-2">
      <div v-if="allowPickerAdd && !showTypeahead && displayedServices.length">
        <service-picker
          :selected-ids="selectedServiceIds"
          :type="pickerType"
          :preset-employee="presetEmployee"
          @add="handleAddFromPicker"
          @remove="handleRemoveFromPicker"
          @clear-preset-employee="handleClearPresetEmployee"
        />
      </div>

      <div v-if="$grants.all({ customer: ['profile'] }) && staging.customer">
        <recents v-model:staging="_staging" />
      </div>
    </div>

    <div v-if="!!associations.length" class="w-full mt-6">
      <div class="mb-4">
        <h3 class="font-semibold mb-1 text-sm uppercase leading-none text-gray-600 dark:text-gray-300">
          {{ $t('service.addOns') }}
        </h3>
        <p class="text-gray-600 leading-none text-sm dark:text-gray-300">
          {{ $t('pages.appointment.create.addOnsInfo', servicesCount) }}
        </p>
      </div>

      <div class="w-full h-full max-h-2xs overflow-y-auto p-0 border rounded-md flex-col divide-y">
        <add-on-select
          v-for="association in associations"
          :key="association.target_id"
          :association="association"
          @add="handleAddAssociation"
        />
      </div>
    </div>

    <o-dialog
      ref="associationDialog"
      :title="$t('pages.appointment.create.addOnPromptTitle')"
      :options="[
        { text: $t('pages.appointment.create.keepRegularPrice'), type: 'cancel' },
        { text: $t('pages.appointment.create.applyAddOnPrice'), type: 'confirm' },
      ]"
    >
      <div v-if="pendingOpportunity" class="text-left space-y-3">
        <p class="text-sm text-gray-600 dark:text-gray-300">
          {{ $t('pages.appointment.create.addOnPromptInfo', {
            target: pendingOpportunity.target.service.title,
            source: pendingOpportunity.source.service.title,
          }) }}
        </p>
        <p class="font-medium text-gray-800 dark:text-gray-100">
          {{ pendingOpportunity.target.service.title }}: {{ pendingDiscountedPrice }}
        </p>
      </div>
    </o-dialog>
  </div>
</template>

<script setup lang="ts">
import Service from '@/components/reservation/create/Service.vue'
import Recents from '@/components/reservation/create/Recents.vue'
import ServiceTypeahead from '@/components/reservation/create/ServiceTypeahead.vue'
import ServicePicker from '@/components/reservation/create/ServicePicker.vue'
import AddOnSelect from '@/components/service/elements/AddOnSelect.vue'
import { ServiceResourceAssignment } from '@/_api/models/serviceResource'
import { ServiceAssociation } from '@/_api/models/serviceAssociation'
import {
  bestAssociationForTarget,
  regularTargetsForSource,
  serviceAssociationSaving,
  type ServiceAssociationOpportunity,
} from '@/utils/serviceAssociationOpportunities'

import { useServiceStore } from '~/stores/useServiceStore'
import { useEmployeeStore } from '~/stores/useEmployeeStore'

interface Props {
  staging?: Record<string, any> | null
  isActive: boolean
}

interface Emits {
  (e: 'update:staging', value: any): void
}

const props = withDefaults(defineProps<Props>(), {
  staging: null,
})

const emit = defineEmits<Emits>()
const { $auth } = useNuxtApp() as any

const serviceStore = useServiceStore()
const employeeStore = useEmployeeStore()
const showTypeahead = ref(true)
const employeePresetCleared = ref(false)
const associationDialog = ref<any>()
const pendingOpportunity = ref<ServiceAssociationOpportunity | null>(null)
const addingServiceFromPicker = ref(false)

const _staging = computed({
  get () {
    return props.staging
  },
  set (value) {
    emit('update:staging', value)
  },
})

const servicesCount = computed(() => {
  return props.staging?.services?.filter((service: any) => !service.association).length ?? 0
})

const displayedServices = computed(() => {
  const services = _staging.value?.services || []
  const out: any[] = []
  const seen: Record<string, number> = {}

  for (let i = 0; i < services.length; i++) {
    const entry = services[i]
    if (!entry?.service) continue

    const id = entry.service.idService ?? entry.service.id ?? i
    const occurrence = (seen[id] = (seen[id] || 0) + 1)
    out.push({ index: i, service: entry, key: `${id}-${occurrence}` })
  }

  return out
})

const selectedServiceIds = computed(() => {
  const services = _staging.value?.services || []
  return services
    .map((entry: any) => entry?.service?.idService ?? entry?.service?.id)
    .filter((id: any) => id != null)
})

const pickerType = computed(() => {
  if (_staging.value?.hasGroupService) return 'group'
  if (_staging.value?.hasSingleService) return 'single'
  if (_staging.value?.isCustomerTypeGroup) return 'group'
  return null
})

const pendingDiscountedPrice = computed(() => {
  const opportunity = pendingOpportunity.value
  if (!opportunity) return ''

  const discountedPrice = ServiceAssociation.formatDiscountedPrice(
    {
      ...opportunity.association,
      target: {
        ...opportunity.target.service,
        price: opportunity.targetPrice,
      },
    },
    $auth?.info?.location?.currencySymbol || '',
  )

  return discountedPrice
    || opportunity.target.service.priceLabel
    || ($auth?.info?.location?.currencySymbol || '') + Number(opportunity.target.service.price || 0).toFixed(2)
})

const associations = computed(() => {
  const services = Array.isArray(props.staging?.services)
    ? props.staging.services
    : Object.values(props.staging?.services || {})
  const selectedIds = new Set(
    services
      .map((entry: any) => entry?.service?.idService ?? entry?.service?.id)
      .filter(Boolean),
  )
  const items = services.reduce((acc: Record<string, any>, entry: any) => {
    if (entry?.association) {
      return acc
    }

    const sourceAssociations = entry?.service?.sourceAssociations

    if (!Array.isArray(sourceAssociations) || !sourceAssociations.length) {
      return acc
    }

    for (const association of sourceAssociations) {
      const targetId = association.target_id

      if (selectedIds.has(targetId)) {
        continue
      }

      if (acc[targetId]) {
        const existingSaving = serviceAssociationSaving(acc[targetId], acc[targetId].target)
        const candidateSaving = serviceAssociationSaving(association, association.target)

        if (existingSaving >= candidateSaving) {
          continue
        }
      }

      acc[targetId] = association
    }

    return acc
  }, {})

  return Object.values(items)
})

const allowAdd = computed(() => {
  return hasCompleteServices.value && !hasGroupService.value
})

const allowPickerAdd = computed(() => {
  return !hasGroupService.value
})

const hasService = computed(() => {
  return !!_staging.value.services.length
})

const hasMultipleServices = computed(() => {
  return _staging.value.services.length > 1
})

const hasCompleteServices = computed(() => {
  return _staging.value.services.every((service: any) => !!service.service)
})

const hasSingleService = computed(() => {
  return _staging.value.hasSingleService
})

const hasGroupService = computed(() => {
  return _staging.value.hasGroupService
})

const presetEmployee = computed(() => {
  const explicitPreset = _staging.value?.presets?.employee

  if (explicitPreset) {
    return explicitPreset
  }

  if (employeePresetCleared.value) {
    return null
  }

  const services = Array.isArray(_staging.value?.services)
    ? _staging.value.services
    : Object.values(_staging.value?.services || {})
  const previousService = services
    .slice()
    .reverse()
    .find((entry: any) => entry?.service && !entry?.association)

  return previousService?.employee || null
})

watch(() => displayedServices.value.length, (len) => {
  if (!len) {
    showTypeahead.value = true
  }
})

function handleTypeaheadClose () {
  if (displayedServices.value.length) {
    showTypeahead.value = false
  }
}

function handleClearPresetEmployee () {
  employeePresetCleared.value = true

  if (_staging.value?.presets) {
    _staging.value.presets.employee = null
  }
}

function handleAddService () {
  _staging.value.addReservationService()
}

function handleRemoveService (index: number) {
  _staging.value.services.splice(index, 1)
}

function handleAddAssociation (association: any) {
  _staging.value.addReservationService({
    service: association.target,
    association,
  })
}

async function handleAddFromPicker (service: any) {
  if (!service || addingServiceFromPicker.value) return
  addingServiceFromPicker.value = true

  try {
    await addFromPicker(service)
  } finally {
    addingServiceFromPicker.value = false
  }
}

async function promptForAssociation (opportunity: ServiceAssociationOpportunity) {
  pendingOpportunity.value = opportunity
  await nextTick()

  const response = await associationDialog.value.open()
  pendingOpportunity.value = null

  return response === 'confirm'
}

function addService (service: any, association: any = null) {
  const staging = _staging.value
  const emptyIndex = staging.services.findIndex((entry: any) => !entry?.service)
  let entry

  if (emptyIndex >= 0) {
    entry = staging.services[emptyIndex]
    entry.service = service
    entry.employee = presetEmployee.value || 0

    if (typeof entry.setValues === 'function') {
      entry.setValues()
    }

    if (typeof entry.setResources === 'function') {
      const assignment = staging.isCustomerTypeGroup || service.isServiceCustomerTypeSingle
        ? ServiceResourceAssignment.reservation
        : ServiceResourceAssignment.customer
      entry.setResources(assignment)
    }

    if (association) {
      staging.applyAssociation(entry, association)
    }

    staging.allowInternet = service.allowInternet
    staging.customerMax = service.customerMax
  } else {
    const values: Record<string, any> = {
      service,
      employee: presetEmployee.value || 0,
    }
    if (association) values.association = association

    staging.addReservationService(values)
    entry = staging.services[staging.services.length - 1]
  }

  return entry
}

function servicePriceForEmployee (service: any, employee: any) {
  return Number(employee?.getEmployeeServiceForService?.(service)?.price ?? service?.price ?? 0)
}

async function addFromPicker (service: any) {
  const staging = _staging.value
  const placeholder = { service }
  const opportunity = bestAssociationForTarget(
    staging.services,
    service,
    null,
    servicePriceForEmployee(service, presetEmployee.value),
  )
  const association = opportunity && await promptForAssociation({
    ...opportunity,
    target: placeholder,
  })
    ? opportunity.association
    : null

  const added = addService(service, association)
  if (added.association) return

  for (const target of regularTargetsForSource(staging.services, added)) {
    const reverseOpportunity = bestAssociationForTarget(
      staging.services,
      target.service,
      target,
      servicePriceForEmployee(target.service, target.employee),
    )
    if (reverseOpportunity && await promptForAssociation(reverseOpportunity)) {
      staging.applyAssociation(target, reverseOpportunity.association)
    }
  }
}

function handleRemoveFromPicker (service: any) {
  if (!service) return

  const targetId = service.idService ?? service.id
  const index = _staging.value.services.findIndex(
    (entry: any) => (entry?.service?.idService ?? entry?.service?.id) === targetId,
  )

  if (index >= 0) {
    _staging.value.services.splice(index, 1)
  }
}

// created logic
serviceStore.sync()
employeeStore.sync()
</script>
